import React, { useEffect, useState } from "react";
import { Sparkles, Loader2, Download, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, aiJob } from "@/lib/api";
import { PageHeader, SectionCard, RiskBadge } from "@/components/Primitives";
import DataTable from "@/components/DataTable";
import { AIOutputDisclaimer } from "@/components/Disclaimer";
import AIFeedback from "@/components/AIFeedback";
import ShowReasoning from "@/components/ShowReasoning";
import { exportToPDF } from "@/lib/pdf";

export default function CareCoordination() {
  const [discharges, setDischarges] = useState([]);
  const [risk, setRisk] = useState([]);
  const [gaps, setGaps] = useState([]);
  const [patients, setPatients] = useState([]);
  const [form, setForm] = useState({ patient_id: "", diagnosis: "", post_acute_level: "Home Health", follow_up_date: "" });
  const [toc, setToc] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/care/discharges").then(r=>setDischarges(r.data));
  useEffect(()=>{
    load();
    api.get("/care/readmission-risk").then(r=>setRisk(r.data));
    api.get("/care/gaps").then(r=>setGaps(r.data));
    api.get("/patients").then(r=>{ setPatients(r.data); setForm(f=>({...f, patient_id: r.data[0]?.id||""})); });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    await api.post("/care/discharges", form);
    toast.success("Discharge plan saved");
    load();
  };

  const genTOC = async () => {
    setBusy(true);
    try {
      const data = await aiJob("/care/transition-of-care", form);
      setToc({ text: data.final, score: data.score, reasoning: data.reasoning, verification_footer: data.verification_footer });
      toast.success("Transition of Care document drafted");
    } catch { toast.error("Generation failed"); }
    finally { setBusy(false); }
  };

  const riskCols = [
    { key: "patient_name", label: "Patient" },
    { key: "risk", label: "Risk", render: (r)=><RiskBadge risk={r.risk} /> },
    { key: "score", label: "Score", render: (r)=>r.score.toFixed(2) },
  ];

  const gapCols = [
    { key: "patient_name", label: "Patient" },
    { key: "gap", label: "Care Gap" },
    { key: "overdue_days", label: "Overdue (days)", render: (r)=><span className={r.overdue_days>60?"text-red-600 font-medium":""}>{r.overdue_days}</span> },
  ];

  return (
    <>
      <PageHeader eyebrow="Module 10" title="Care Coordination"
        subtitle="Discharge planning, readmission risk scoring, transition of care and overdue follow-ups." testid="care-page" />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <SectionCard title="Discharge Planning" testid="discharge-card">
          <form onSubmit={submit} className="space-y-2">
            <select value={form.patient_id} onChange={(e)=>setForm({...form, patient_id:e.target.value})}
              className="w-full px-3 py-2 bg-white border border-brand rounded-md text-sm">
              {patients.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <input placeholder="Diagnosis" value={form.diagnosis} onChange={(e)=>setForm({...form, diagnosis:e.target.value})} required
              className="w-full px-3 py-2 bg-white border border-brand rounded-md text-sm" data-testid="discharge-dx" />
            <select value={form.post_acute_level} onChange={(e)=>setForm({...form, post_acute_level:e.target.value})}
              className="w-full px-3 py-2 bg-white border border-brand rounded-md text-sm">
              <option>Home</option><option>Home Health</option><option>SNF</option><option>Inpatient Rehab</option><option>LTAC</option>
            </select>
            <input type="date" value={form.follow_up_date?.split("T")[0]||""} onChange={(e)=>setForm({...form, follow_up_date: e.target.value+"T00:00:00Z"})} required
              className="w-full px-3 py-2 bg-white border border-brand rounded-md text-sm" data-testid="discharge-followup" />
            <div className="flex gap-2">
              <button type="submit" className="flex-1 bg-brand-primary text-white py-2 rounded-md text-sm font-medium hover:bg-[#35096B]"><Plus className="w-4 h-4 inline mr-1" />Save</button>
              <button type="button" onClick={genTOC} disabled={busy} className="flex-1 bg-brand-teal text-white py-2 rounded-md text-sm font-medium inline-flex items-center justify-center gap-1.5" data-testid="gen-toc-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} TOC
              </button>
            </div>
          </form>
        </SectionCard>

        <div className="lg:col-span-2">
          <SectionCard title="Transition of Care Document" testid="toc-card"
            actions={toc && <button onClick={()=>exportToPDF("Transition of Care", toc.text)} className="text-xs px-3 py-1.5 rounded-md border border-brand hover:bg-brand-bg inline-flex items-center gap-1.5"><Download className="w-3.5 h-3.5" /> PDF</button>}>
            {toc ? (
              <>
                <AIOutputDisclaimer />
                <pre className="text-sm whitespace-pre-wrap leading-relaxed text-brand-text max-h-72 overflow-y-auto" data-testid="toc-text">{toc.text}</pre>
                {toc.verification_footer && (
                  <div className="mt-3 px-3 py-2 rounded-md bg-amber-50 border border-amber-300 text-xs text-amber-900 italic" data-testid="toc-verification-footer">{toc.verification_footer}</div>
                )}
                <ShowReasoning reasoning={toc.reasoning} score={toc.score} testid="toc-reasoning" />
                <AIFeedback module="care-coordination" outputType="toc_document" testid="toc-feedback" />
              </>
            ) : <div className="text-sm text-brand-muted">Use the TOC button to generate a transition-of-care document.</div>}
          </SectionCard>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard title="Readmission Risk" testid="risk-section">
          <DataTable data={risk} columns={riskCols} searchKeys={["patient_name","risk"]} testid="risk-table" />
        </SectionCard>
        <SectionCard title="Care Gap Tracker" testid="gaps-section">
          <DataTable data={gaps} columns={gapCols} searchKeys={["patient_name","gap"]} testid="gaps-table" />
        </SectionCard>
      </div>

      {discharges.length>0 && (
        <SectionCard title="Discharge Plans" testid="discharge-list" >
          <DataTable data={discharges} columns={[
            { key: "patient_id", label: "Patient", render: (r)=>patients.find(p=>p.id===r.patient_id)?.name||"—" },
            { key: "diagnosis", label: "Diagnosis" },
            { key: "post_acute_level", label: "Level" },
            { key: "follow_up_date", label: "Follow-up", render: (r)=>new Date(r.follow_up_date).toLocaleDateString() },
          ]} searchKeys={["diagnosis"]} testid="discharge-table" />
        </SectionCard>
      )}
    </>
  );
}
